import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@monksflow/monks-ui';

import { AgentsService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import type { CategoryId } from '@/shared/constants/category-colors';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { useSaveAgentOverride } from '@/features/admin/model/useSaveAgentOverride';
import { getAgentOverrides } from '@/features/admin/api/agent-overrides.api';
import { resolveEffectiveAgentMetadata } from '@/features/admin/model/resolveEffectiveAgentMetadata';
import { useAuth } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { getAdminToolsListDisplayName } from '@/features/admin/lib/canonicalAdminToolLabels';
import { useAgentCategoryOptions } from '@/features/admin/model/useAgentCategoryOptions';
import { useChatStore } from '@/features/chats/stores';
import { ROUTES } from '@/shared/config/routes';

import { AdminPageShell } from '../AdminPageShell';
import { ExternalToolsPage, ORG_WIDE_SCOPE } from './ExternalTools';

export function ExternalToolsAgentConfigPage() {
  const { t } = useTranslation('admin');
  const agentId = SPECIAL_AGENT_IDS.EXTERNAL_TOOLS;
  const categoryOptions = useAgentCategoryOptions();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const [selectedScope, setSelectedScope] = useState<string | null>(null);
  const reloadCurrentAgentIfActive = useCallback(async (savedAgentId: string) => {
    if (useChatStore.getState().currentAgentId === savedAgentId) {
      await useChatStore.getState().reloadCurrentAgent();
    }
  }, []);
  const { mutateAsync: saveAgentOverride } = useSaveAgentOverride({ reloadCurrentAgentIfActive });
  const organizationId = useAuth((state) => state.signedInUser.value?.organization.id);

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ADMIN_QUERY_KEYS.tools.agents,
    queryFn: async () => {
      const response = await AgentsService.getAllAgents({
        client: inferenceHttp,
        query: { includeNonVisibleAgents: false, showAll: true },
      });
      return response.data ?? [];
    },
  });

  const { data: overrides = [] } = useQuery({
    queryKey: [ADMIN_QUERY_KEYS.tools.agents, 'overrides', agentId],
    queryFn: async () => {
      const result = await getAgentOverrides(agentId);
      return result;
    },
  });

  const agent = agents.find((a) => a.id === agentId);

  const activeScope = selectedScope ?? currentWorkspaceId ?? ORG_WIDE_SCOPE;

  const { effectiveName, effectiveDescription, effectiveTag } = useMemo(() => {
    const metadata = resolveEffectiveAgentMetadata(overrides, {
      organizationId,
      workspaceId: activeScope === ORG_WIDE_SCOPE ? undefined : activeScope,
    });

    return {
      effectiveName: metadata.name ?? agent?.name ?? '',
      effectiveDescription: metadata.userFacingDescription ?? agent?.description ?? '',
      effectiveTag: (metadata.tag ?? agent?.tag ?? 'plan') satisfies CategoryId,
    };
  }, [activeScope, overrides, agent, organizationId]);

  const handleSaveBasicInfo = async (
    data: { name: string; description: string; category?: CategoryId },
    workspaceId?: string,
  ) => {
    await saveAgentOverride({
      agentId,
      metadata: {
        name: data.name,
        userFacingDescription: data.description,
        ...(data.category ? { tag: data.category } : {}),
      },
      workspaceId,
      organizationId,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner className="size-8" />
      </div>
    );
  }

  const title = getAdminToolsListDisplayName(agentId, agent?.name ?? '');

  // `ownsBodyInset`: the padded scroll region below is this page's inset in both frames,
  // and it is exactly the one the shell would otherwise add.
  return (
    <AdminPageShell
      title={title}
      parents={[{ label: t('admin.adminLayout.tools'), href: ROUTES.ADMIN_TOOLS }]}
      ownsBodyInset
    >
      <div className="flex-1 px-6 py-6 overflow-y-auto">
        <ExternalToolsPage
          toolName={effectiveName}
          toolDescription={effectiveDescription}
          onSaveBasicInfo={handleSaveBasicInfo}
          onScopeChange={setSelectedScope}
          category={effectiveTag}
          categoryOptions={categoryOptions}
        />
      </div>
    </AdminPageShell>
  );
}
